/**
 * useMetrics Hook
 * React hook for recording metrics events
 * Provides convenient access to metrics recording with type safety
 */

import { useCallback } from 'react';
import { recordEvent, identifyUser, recordPageView, recordConversionEvent } from '../services/metrics';
import { MetricsEventProperties, MetricsEventNames } from '../types/metrics';
import { Product, Coupon } from '../types';

/**
 * Custom hook for metrics recording
 * Returns helper functions for common event types
 */
export const useMetrics = () => {
    /**
     * Record a generic event
     * Completely safe - handles null/undefined inputs gracefully, never throws
     */
    const recordEventCallback = useCallback((
        eventName: string | null | undefined,
        properties: MetricsEventProperties = {}
    ) => {
        try {
            const safeEventName = (eventName || 'unknown_event').toString();
            const safeProperties = properties && typeof properties === 'object' ? properties : {};
            recordEvent(safeEventName, safeProperties);
        } catch (error) {
            // Metrics failures should not affect main app
        }
    }, []);

    /**
     * Record signup step viewed
     * Completely safe - handles invalid inputs gracefully, never throws
     */
    const recordStepViewed = useCallback((step: number | null | undefined, properties: MetricsEventProperties = {}) => {
        try {
            const safeStep = typeof step === 'number' && !isNaN(step) ? step : 0;
            recordEvent(MetricsEventNames.SIGNUP_STEP_VIEWED, {
                step: safeStep,
                step_name: `step_${safeStep}`,
                ...(properties && typeof properties === 'object' ? properties : {}),
            });
        } catch (error) {
            // Metrics failures should not affect main app
        }
    }, []);

    /**
     * Record signup step completed
     * Completely safe - handles invalid inputs gracefully, never throws
     */
    const recordStepCompleted = useCallback((step: number | null | undefined, properties: MetricsEventProperties = {}) => {
        try {
            const safeStep = typeof step === 'number' && !isNaN(step) ? step : 0;
            recordEvent(MetricsEventNames.SIGNUP_STEP_COMPLETED, {
                step: safeStep,
                step_name: `step_${safeStep}`,
                ...(properties && typeof properties === 'object' ? properties : {}),
            });
        } catch (error) {
            // Metrics failures should not affect main app
        }
    }, []);

    /**
     * Record product selection
     * Completely safe - handles null/undefined inputs gracefully, never throws
     */
    const recordProductSelected = useCallback((
        productId: string | null | undefined,
        productName: string | null | undefined,
        billingCycle: 'monthly' | 'annual' | null | undefined,
        priceId: string | null | undefined,
        properties: MetricsEventProperties = {}
    ) => {
        try {
            recordEvent(MetricsEventNames.PRODUCT_SELECTED, {
                product_id: (productId || '').toString(),
                product_name: (productName || '').toString(),
                billing_cycle: (billingCycle === 'monthly' || billingCycle === 'annual') ? billingCycle : 'monthly',
                price_id: (priceId || '').toString(),
                ...(properties && typeof properties === 'object' ? properties : {}),
            });
        } catch (error) {
            // Fail silently - analytics should never break the app
            if (process.env.NODE_ENV === 'development') {
                try {
                    console.warn('recordProductSelected failed:', error);
                } catch (consoleError) {
                    // Fail silently
                }
            }
        }
    }, []);

    /**
     * Record payment method creation
     * Completely safe - handles invalid inputs gracefully, never throws
     */
    const recordPaymentMethodCreated = useCallback((properties: MetricsEventProperties = {}) => {
        try {
            recordEvent(MetricsEventNames.PAYMENT_METHOD_CREATED, 
                properties && typeof properties === 'object' ? properties : {});
        } catch (error) {
            // Fail silently - analytics should never break the app
            if (process.env.NODE_ENV === 'development') {
                try {
                    console.warn('recordPaymentMethodCreated failed:', error);
                } catch (consoleError) {
                    // Fail silently
                }
            }
        }
    }, []);

    /**
     * Record payment method failure
     * Completely safe - handles null/undefined inputs gracefully, never throws
     */
    const recordPaymentMethodFailed = useCallback((error: string | null | undefined, properties: MetricsEventProperties = {}) => {
        try {
            recordEvent(MetricsEventNames.PAYMENT_METHOD_FAILED, {
                error_message: (error || 'Unknown error').toString(),
                ...(properties && typeof properties === 'object' ? properties : {}),
            });
        } catch (error) {
            // Fail silently - analytics should never break the app
            if (process.env.NODE_ENV === 'development') {
                try {
                    console.warn('recordPaymentMethodFailed failed:', error);
                } catch (consoleError) {
                    // Fail silently
                }
            }
        }
    }, []);

    /**
     * Record signup started
     * Completely safe - handles invalid inputs gracefully, never throws
     */
    const recordSignupStarted = useCallback((properties: MetricsEventProperties = {}) => {
        try {
            recordEvent(MetricsEventNames.SIGNUP_STARTED, 
                properties && typeof properties === 'object' ? properties : {});
        } catch (error) {
            // Fail silently - analytics should never break the app
            if (process.env.NODE_ENV === 'development') {
                try {
                    console.warn('recordSignupStarted failed:', error);
                } catch (consoleError) {
                    // Fail silently
                }
            }
        }
    }, []);

    /**
     * Record signup completed
     * Simple event indicating the signup process is complete
     * Completely safe - handles null/undefined inputs gracefully, never throws
     */
    const recordSignupCompleted = useCallback((properties: MetricsEventProperties = {}) => {
        try {
            recordEvent(MetricsEventNames.SIGNUP_COMPLETED, 
                properties && typeof properties === 'object' ? properties : {});
        } catch (error) {
            // Metrics failures should not affect main app
        }
    }, []);

    /**
     * Record signup failed
     * Completely safe - handles null/undefined inputs gracefully, never throws
     */
    const recordSignupFailed = useCallback((error: string | null | undefined, properties: MetricsEventProperties = {}) => {
        try {
            recordEvent(MetricsEventNames.SIGNUP_FAILED, {
                error_message: (error || 'Unknown error').toString(),
                ...(properties && typeof properties === 'object' ? properties : {}),
            });
        } catch (error) {
            // Fail silently - analytics should never break the app
            if (process.env.NODE_ENV === 'development') {
                try {
                    console.warn('recordSignupFailed failed:', error);
                } catch (consoleError) {
                    // Fail silently
                }
            }
        }
    }, []);

    /**
     * Record conversion completed (success page reached)
     * Accepts either simple membership tier or full purchase data
     * Processing happens in service layer, not UI
     * Completely safe - handles null/undefined inputs gracefully, never throws
     */
    const recordConversionCompleted = useCallback((
        productOrTier: Product | string | null | undefined,
        priceIdOrProperties?: string | MetricsEventProperties | null | undefined,
        options?: {
            coupon?: Coupon | null;
            giftCode?: any | null;
            invoicePreview?: any | null;
            additionalProperties?: MetricsEventProperties;
        }
    ) => {
        try {
            // Check if first param is a Product (has pricing property) or a string (membership tier)
            const isProduct = productOrTier && typeof productOrTier === 'object' && 'pricing' in productOrTier;
            
            if (isProduct && priceIdOrProperties && typeof priceIdOrProperties === 'string') {
                // Full purchase data path - pass raw data to service layer
                const product = productOrTier as Product;
                const priceId = priceIdOrProperties;
                
                recordConversionEvent(
                    MetricsEventNames.CONVERSION_COMPLETED,
                    {
                        product,
                        priceId,
                        coupon: options?.coupon,
                        giftCode: options?.giftCode,
                        invoicePreview: options?.invoicePreview,
                    },
                    options?.additionalProperties || {}
                );
            } else {
                // Simple membership tier path (backward compatibility)
                const membershipTier = typeof productOrTier === 'string' ? productOrTier : (productOrTier as Product)?.title || (productOrTier as Product)?.name || 'unknown';
                const properties = typeof priceIdOrProperties === 'object' ? priceIdOrProperties : {};
                
                recordEvent(MetricsEventNames.CONVERSION_COMPLETED, {
                    membership_tier: membershipTier.toString(),
                    ...(properties && typeof properties === 'object' ? properties : {}),
                });
            }
        } catch (error) {
            // Metrics failures should not affect main app
        }
    }, []);

    /**
     * Record error occurrence
     * Completely safe - handles null/undefined inputs gracefully, never throws
     */
    const recordError = useCallback((
        errorMessage: string | null | undefined,
        errorType: string | null | undefined,
        properties: MetricsEventProperties = {}
    ) => {
        try {
            recordEvent(MetricsEventNames.ERROR_OCCURRED, {
                error_message: (errorMessage || 'Unknown error').toString(),
                error_type: (errorType || 'unknown').toString(),
                ...(properties && typeof properties === 'object' ? properties : {}),
            });
        } catch (error) {
            // Fail silently - analytics should never break the app
            if (process.env.NODE_ENV === 'development') {
                try {
                    console.warn('recordError failed:', error);
                } catch (consoleError) {
                    // Fail silently
                }
            }
        }
    }, []);

    /**
     * Record button click
     * Completely safe - handles null/undefined inputs gracefully, never throws
     */
    const recordButtonClick = useCallback((
        buttonName: string | null | undefined,
        location: string | null | undefined,
        properties: MetricsEventProperties = {}
    ) => {
        try {
            recordEvent(MetricsEventNames.BUTTON_CLICKED, {
                button_name: (buttonName || 'unknown').toString(),
                location: (location || 'unknown').toString(),
                ...(properties && typeof properties === 'object' ? properties : {}),
            });
        } catch (error) {
            // Fail silently - analytics should never break the app
            if (process.env.NODE_ENV === 'development') {
                try {
                    console.warn('recordButtonClick failed:', error);
                } catch (consoleError) {
                    // Fail silently
                }
            }
        }
    }, []);

    /**
     * Record form field completion
     * Completely safe - handles null/undefined inputs gracefully, never throws
     */
    const recordFormFieldCompleted = useCallback((
        fieldName: string | null | undefined,
        formName: string | null | undefined,
        properties: MetricsEventProperties = {}
    ) => {
        try {
            recordEvent(MetricsEventNames.FORM_FIELD_COMPLETED, {
                field_name: (fieldName || 'unknown').toString(),
                form_name: (formName || 'unknown').toString(),
                ...(properties && typeof properties === 'object' ? properties : {}),
            });
        } catch (error) {
            // Fail silently - analytics should never break the app
            if (process.env.NODE_ENV === 'development') {
                try {
                    console.warn('recordFormFieldCompleted failed:', error);
                } catch (consoleError) {
                    // Fail silently
                }
            }
        }
    }, []);

    /**
     * Identify user
     * Completely safe - handles null/undefined inputs gracefully, never throws
     */
    const identifyUserCallback = useCallback((userId: string | null | undefined, traits: Record<string, any> = {}) => {
        try {
            const safeUserId = (userId || '').toString();
            const safeTraits = traits && typeof traits === 'object' ? traits : {};
            if (safeUserId) {
                identifyUser(safeUserId, safeTraits);
            }
        } catch (error) {
            // Fail silently - analytics should never break the app
            if (process.env.NODE_ENV === 'development') {
                try {
                    console.warn('identifyUserCallback failed:', error);
                } catch (consoleError) {
                    // Fail silently
                }
            }
        }
    }, []);

    /**
     * Record page view
     * Completely safe - handles null/undefined inputs gracefully, never throws
     */
    const recordPageViewCallback = useCallback((name: string | null | undefined, properties: Record<string, any> = {}) => {
        try {
            const safeName = (name || 'unknown').toString();
            const safeProperties = properties && typeof properties === 'object' ? properties : {};
            recordPageView(safeName, safeProperties);
        } catch (error) {
            // Fail silently - analytics should never break the app
            if (process.env.NODE_ENV === 'development') {
                try {
                    console.warn('recordPageViewCallback failed:', error);
                } catch (consoleError) {
                    // Fail silently
                }
            }
        }
    }, []);

    return {
        // Generic event recording
        recordEvent: recordEventCallback,
        
        // Signup funnel helpers
        recordStepViewed,
        recordStepCompleted,
        recordSignupStarted,
        recordSignupCompleted,
        recordSignupFailed,
        
        // Product helpers
        recordProductSelected,
        
        // Payment helpers
        recordPaymentMethodCreated,
        recordPaymentMethodFailed,
        
        // Conversion helpers
        recordConversionCompleted,
        
        // Error helpers
        recordError,
        
        // User action helpers
        recordButtonClick,
        recordFormFieldCompleted,
        
        // User identification
        identify: identifyUserCallback,
        
        // Page tracking
        recordPageView: recordPageViewCallback,
    };
};

